/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";
import { getDatesWithoutWeekends, hitungtotaljamkerja } from "utils/func";

function UsersKhususApel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tanggalAwal, setTanggalAwal] = useState("");
  const [tanggalAkhir, setTanggalAkhir] = useState("");
  const [listLibur, setListLibur] = useState([]);
  const [totalHariKerja, setTotalHariKerja] = useState(0);
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getLibur = useCallback(() => {
    axios({
      method: "get",
      url: "https://smartservice.rotendaokab.go.id/api/admin/libur",
      timeout: API_TIMEOUT,
      headers: {
        Authorization: `Bearer ${""}`,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setListLibur(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching libur data:", error);
      });
  }, []);

  // Set default dates on component mount
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    setTanggalAwal(firstDay.toISOString().split("T")[0]);
    setTanggalAkhir(today.toISOString().split("T")[0]);

    // Auto-load data with default dates
    fetchData(
      firstDay.toISOString().split("T")[0],
      today.toISOString().split("T")[0]
    );

    getLibur();
  }, []);

  const fetchData = async (tanggalAwal, tanggalAkhir) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}apel-harian/users-khusus/apel`,
        {
          params: {
            tanggal_awal: tanggalAwal,
            tanggal_akhir: tanggalAkhir,
          },
        }
      );

      if (response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data apel harian users khusus",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetData = () => {
    if (!tanggalAwal || !tanggalAkhir) {
      swal({
        title: "Error",
        text: "Tanggal awal dan tanggal akhir harus diisi",
        icon: "error",
      });
      return;
    }

    if (new Date(tanggalAwal) > new Date(tanggalAkhir)) {
      swal({
        title: "Error",
        text: "Tanggal awal tidak boleh lebih besar dari tanggal akhir",
        icon: "error",
      });
      return;
    }

    fetchData(tanggalAwal, tanggalAkhir);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Hitung total hari kerja berdasarkan data libur
  useEffect(() => {
    if (listLibur.length > 0 && tanggalAwal && tanggalAkhir) {
      const datesWithoutWeekends = getDatesWithoutWeekends(
        tanggalAwal,
        tanggalAkhir,
        listLibur
      );
      setTotalHariKerja(datesWithoutWeekends.length);
    }
  }, [listLibur, tanggalAwal, tanggalAkhir]);

  // Fungsi untuk menghitung persentase berdasarkan hari kerja yang benar
  const calculateCorrectPercentage = (hadir, totalHariKerja) => {
    if (!totalHariKerja || totalHariKerja === 0) return 0;
    return ((hadir || 0) / totalHariKerja) * 100;
  };

  const handleDetailClick = (pegawai) => {
    // Navigate to detail page
    navigate(
      `/daily-performance/apel-harian/perorang-detail/${pegawai.uid_RSS}`
    );
  };

  const handleExportExcel = () => {
    if (!data || !data.data_pegawai) {
      swal({
        title: "Error",
        text: "Tidak ada data untuk diekspor",
        icon: "error",
      });
      return;
    }

    try {
      // Create HTML table
      let tableHTML = `
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Nama Pegawai</th>
              <th>Jabatan</th>
              <th>Dinas</th>
              <th>Bidang</th>
              <th>Pangkat</th>
              <th>Total Apel Pagi</th>
              <th>Total Apel Siang</th>
              <th>Hadir Pagi</th>
              <th>Hadir Siang</th>
              <th>Persentase Hadir Pagi</th>
              <th>Persentase Hadir Siang</th>
            </tr>
          </thead>
          <tbody>
      `;

      data.data_pegawai.forEach((pegawai, index) => {
        tableHTML += `
          <tr>
            <td>${index + 1}</td>
            <td>${pegawai.nama_pegawai}</td>
            <td>${pegawai.nama_jabatan}</td>
            <td>${pegawai.nama_dinas}</td>
            <td>${pegawai.nama_bidang}</td>
            <td>${pegawai.pangkat}</td>
            <td>${pegawai.total_apel_pagi}</td>
            <td>${pegawai.total_apel_siang}</td>
            <td>${pegawai.hadir_pagi}</td>
            <td>${pegawai.hadir_siang}</td>
            <td>${pegawai.persentase_kehadiran_pagi}%</td>
            <td>${pegawai.persentase_kehadiran_siang}%</td>
          </tr>
        `;
      });

      tableHTML += `
          </tbody>
        </table>
      `;

      // Convert to Excel
      const blob = new Blob([tableHTML], { type: "application/vnd.ms-excel" });
      const fileName = `Apel_Harian_Users_Khusus_${tanggalAwal}_${tanggalAkhir}.xls`;
      saveAs(blob, fileName);

      swal({
        title: "Success",
        text: "Data berhasil diekspor ke Excel",
        icon: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      swal({
        title: "Error",
        text: "Gagal mengekspor data",
        icon: "error",
      });
    }
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "15%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "12%" },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    {
      Header: "Apel Pagi",
      accessor: "apel_pagi",
      width: "12%",
      Cell: ({ value }) => (
        <MDBox>
          <MDTypography variant="caption" color="text">
            Hadir: {value?.hadir || 0} / {totalHariKerja || 0}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            {Math.round(
              calculateCorrectPercentage(value?.hadir, totalHariKerja)
            )}
            %
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Apel Siang",
      accessor: "apel_siang",
      width: "12%",
      Cell: ({ value }) => (
        <MDBox>
          <MDTypography variant="caption" color="text">
            Hadir: {value?.hadir || 0} / {totalHariKerja || 0}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            {Math.round(
              calculateCorrectPercentage(value?.hadir, totalHariKerja)
            )}
            %
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Persentase Rata-rata",
      accessor: "persentase_rata_rata",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={`${value}%`}
          color={
            parseFloat(value) >= 80
              ? "success"
              : parseFloat(value) >= 60
              ? "warning"
              : "error"
          }
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Detail",
      accessor: "detail",
      width: "8%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => handleDetailClick(row.original)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  const rows = useMemo(() => {
    if (!data?.data_pegawai) return [];

    const mappedData = data.data_pegawai.map((pegawai, index) => ({
      id: pegawai.uid_RSS || `pegawai-${index}`,
      no: index + 1,
      uid_RSS: pegawai.uid_RSS,
      nama_pegawai: pegawai.nama_pegawai,
      nama_jabatan: pegawai.nama_jabatan,
      pangkat: pegawai.pangkat,
      apel_pagi: {
        hadir: pegawai.hadir_pagi,
        total: pegawai.total_apel_pagi,
        persentase: pegawai.persentase_kehadiran_pagi,
      },
      apel_siang: {
        hadir: pegawai.hadir_siang,
        total: pegawai.total_apel_siang,
        persentase: pegawai.persentase_kehadiran_siang,
      },
      persentase_rata_rata: Math.round(
        (parseFloat(pegawai.persentase_kehadiran_pagi) +
          parseFloat(pegawai.persentase_kehadiran_siang)) /
          2
      ),
    }));

    // Urutkan berdasarkan persentase rata-rata dari tertinggi ke terendah
    return mappedData.sort((a, b) => {
      const persentaseA = parseFloat(a.persentase_rata_rata) || 0;
      const persentaseB = parseFloat(b.persentase_rata_rata) || 0;
      return persentaseB - persentaseA; // Descending order (tertinggi ke terendah)
    });
  }, [data?.data_pegawai]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDTypography variant="h6" gutterBottom>
                    Statistik Apel Harian Pimpinan
                  </MDTypography>
                  <MDBox display="flex" gap={2}>
                    <MDInput
                      type="date"
                      label="Tanggal Awal"
                      value={tanggalAwal}
                      onChange={(e) => setTanggalAwal(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <MDInput
                      type="date"
                      label="Tanggal Akhir"
                      value={tanggalAkhir}
                      onChange={(e) => setTanggalAkhir(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleGetData}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Get Data"
                      )}
                    </MDButton>
                  </MDBox>
                </MDBox>

                {data && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Pimpinan Daerah dan Perangkat Daerah -{" "}
                      {new Date(tanggalAwal).toLocaleDateString("id-ID")} s/d{" "}
                      {new Date(tanggalAkhir).toLocaleDateString("id-ID")}
                    </MDTypography>

                    <Grid container spacing={3} mt={2}>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="info">
                            {data.statistik.total_pegawai}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="success">
                            {data.statistik.total_apel_pagi}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Apel Pagi
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="primary">
                            {data.statistik.total_apel_siang}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Apel Siang
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="warning">
                            {Math.round(
                              calculateCorrectPercentage(
                                data.statistik?.hadir_pagi || 0,
                                totalHariKerja || 0
                              )
                            )}
                            %
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata Persentase Pagi
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="secondary">
                            {Math.round(
                              calculateCorrectPercentage(
                                data.statistik?.hadir_siang || 0,
                                totalHariKerja || 0
                              )
                            )}
                            %
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata Persentase Siang
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="dark">
                            {Math.round(
                              (calculateCorrectPercentage(
                                data.statistik?.hadir_pagi || 0,
                                totalHariKerja || 0
                              ) +
                                calculateCorrectPercentage(
                                  data.statistik?.hadir_siang || 0,
                                  totalHariKerja || 0
                                )) /
                                2
                            )}
                            %
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata Keseluruhan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <MDBox mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <MDTypography variant="button" color="text">
                          Rata-rata Persentase Kehadiran
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          color={
                            Math.round(
                              (calculateCorrectPercentage(
                                data.statistik?.hadir_pagi || 0,
                                totalHariKerja || 0
                              ) +
                                calculateCorrectPercentage(
                                  data.statistik?.hadir_siang || 0,
                                  totalHariKerja || 0
                                )) /
                                2
                            ) >= 80
                              ? "success"
                              : Math.round(
                                  (calculateCorrectPercentage(
                                    data.statistik?.hadir_pagi || 0,
                                    totalHariKerja || 0
                                  ) +
                                    calculateCorrectPercentage(
                                      data.statistik?.hadir_siang || 0,
                                      totalHariKerja || 0
                                    )) /
                                    2
                                ) >= 60
                              ? "warning"
                              : "error"
                          }
                        >
                          {Math.round(
                            (calculateCorrectPercentage(
                              data.statistik?.hadir_pagi || 0,
                              totalHariKerja || 0
                            ) +
                              calculateCorrectPercentage(
                                data.statistik?.hadir_siang || 0,
                                totalHariKerja || 0
                              )) /
                              2
                          )}
                          %
                        </MDTypography>
                      </MDBox>
                      <MDProgress
                        variant="determinate"
                        value={Math.round(
                          (calculateCorrectPercentage(
                            data.statistik?.hadir_pagi || 0,
                            totalHariKerja || 0
                          ) +
                            calculateCorrectPercentage(
                              data.statistik?.hadir_siang || 0,
                              totalHariKerja || 0
                            )) /
                            2
                        )}
                        color={
                          Math.round(
                            (calculateCorrectPercentage(
                              data.statistik?.hadir_pagi || 0,
                              totalHariKerja || 0
                            ) +
                              calculateCorrectPercentage(
                                data.statistik?.hadir_siang || 0,
                                totalHariKerja || 0
                              )) /
                              2
                          ) >= 80
                            ? "success"
                            : Math.round(
                                (calculateCorrectPercentage(
                                  data.statistik?.hadir_pagi || 0,
                                  totalHariKerja || 0
                                ) +
                                  calculateCorrectPercentage(
                                    data.statistik?.hadir_siang || 0,
                                    totalHariKerja || 0
                                  )) /
                                  2
                              ) >= 60
                            ? "warning"
                            : "error"
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {data && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(tanggalAwal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        s/d{" "}
                        {new Date(tanggalAkhir).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {data.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        data?.data_pegawai?.map((pegawai, index) => (
                          <Card
                            key={`${pegawai.uid_RSS || "pegawai"}-${index}`}
                          >
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${pegawai.uid_RSS}.jpg`}
                                      alt={pegawai.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {pegawai.nama_pegawai}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    <Chip
                                      label={`Apel Pagi: ${
                                        pegawai.hadir_pagi
                                      }/${totalHariKerja || 0}`}
                                      color="info"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Apel Siang: ${
                                        pegawai.hadir_siang
                                      }/${totalHariKerja || 0}`}
                                      color="success"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Rata2: ${Math.round(
                                        (parseFloat(
                                          pegawai.persentase_kehadiran_pagi
                                        ) +
                                          parseFloat(
                                            pegawai.persentase_kehadiran_siang
                                          )) /
                                          2
                                      )}%`}
                                      color="warning"
                                      size="small"
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Persentase Kehadiran
                                    </MDTypography>
                                    <MDBox mt={0.5}>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Pagi:{" "}
                                        {pegawai.persentase_kehadiran_pagi}%
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Siang:{" "}
                                        {pegawai.persentase_kehadiran_siang}%
                                      </MDTypography>
                                    </MDBox>
                                    <MDProgress
                                      value={Math.round(
                                        (parseFloat(
                                          pegawai.persentase_kehadiran_pagi
                                        ) +
                                          parseFloat(
                                            pegawai.persentase_kehadiran_siang
                                          )) /
                                          2
                                      )}
                                      color={
                                        Math.round(
                                          (parseFloat(
                                            pegawai.persentase_kehadiran_pagi
                                          ) +
                                            parseFloat(
                                              pegawai.persentase_kehadiran_siang
                                            )) /
                                            2
                                        ) >= 80
                                          ? "success"
                                          : Math.round(
                                              (parseFloat(
                                                pegawai.persentase_kehadiran_pagi
                                              ) +
                                                parseFloat(
                                                  pegawai.persentase_kehadiran_siang
                                                )) /
                                                2
                                            ) >= 60
                                          ? "warning"
                                          : "error"
                                      }
                                      label={`${Math.round(
                                        (parseFloat(
                                          pegawai.persentase_kehadiran_pagi
                                        ) +
                                          parseFloat(
                                            pegawai.persentase_kehadiran_siang
                                          )) /
                                          2
                                      )}%`}
                                      sx={{ height: 8, borderRadius: 8 }}
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Jabatan: {pegawai.nama_jabatan}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Dinas: {pegawai.nama_dinas}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Bidang: {pegawai.nama_bidang}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Pangkat: {pegawai.pangkat}
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDButton
                                      variant="gradient"
                                      color="info"
                                      size="small"
                                      onClick={() =>
                                        handleDetailClick({ original: pegawai })
                                      }
                                      sx={{ minWidth: "auto", px: 1 }}
                                    >
                                      <Icon sx={{ fontSize: 16 }}>
                                        visibility
                                      </Icon>
                                      &nbsp;Detail
                                    </MDButton>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={10}
                      showTotalEntries={true}
                      canSearch={true}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data apel harian users khusus...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!data && !loading && tanggalAwal && tanggalAkhir && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data apel harian users khusus
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih periode tanggal lain atau periksa kembali data
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersKhususApel;
